import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { MessageSquare, AlertCircle } from "lucide-react";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";

export default function RecentChats() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 🧘 Guest Support: If no user, don't even try to fetch. Keep it quiet.
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchChats = async () => {
      try {
        const { data } = await api.get('/conversations');
        // Filter out locally archived chats
        const archivedIds = JSON.parse(localStorage.getItem('chatArchives') || '[]');
        const activeChats = data.conversations.filter(c => !archivedIds.includes(c.id));
        setChats(activeChats.slice(0, 3)); // Top 3
      } catch (err) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    fetchChats();
  }, [user]);

  if (loading) return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-xl shadow h-full flex items-center justify-center">
      <p className="text-gray-400 text-sm animate-pulse">{t('dash_recent_loading')}</p>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow h-full flex flex-col">
      <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-indigo-500" /> {t('dash_recent_chats')}
      </h2>
      {chats.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-4 border border-dashed border-gray-100 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-900/20">
          <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-2 text-2xl animate-float">
            🍃
          </div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('dash_recent_empty_title')}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-4 max-w-[200px] leading-relaxed">
            {user?.role === "PROVIDER" ? t('dash_recent_empty_sub_provider') : t('dash_recent_empty_sub')}
          </p>
          {user?.role !== "PROVIDER" && (
            <div className="flex flex-col gap-2 w-full px-4">
              <Link
                to="/explore"
                className="text-xs px-3 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition font-medium text-center"
              >
                {t('wisdom_cta') || "Enter the Library"}
              </Link>
            </div>
          )}
        </div>
      ) : (
        <ul className="space-y-0.5 sm:space-y-2">
          {chats.map(chat => {
            const myId = user?.id;
            const other = chat.participants.find(p => p.user.id !== myId)?.user;

            let name = other?.name || "Unknown";

            // Ghost mode / Privacy for Seekers:
            if (other?.role === "SEEKER") {
              const isGhost = other?.seeker?.isAnonymous;
              const ghostName = other?.seeker?.username;
              
              // Always prioritize username for seekers if it exists
              name = ghostName || other.name || "Anonymous Seeker";
            }

            // Enhanced Preview Logic: Handle media types nicely
            const lastMsg = chat.lastMessage;
            let lastContent = t('dash_recent_msg_start');

            if (lastMsg) {
              const isMe = lastMsg.senderId === user?.id;
              const prefix = isMe ? t('dash_recent_prefix_me') : "";

              if (lastMsg.type === "IMAGE") {
                lastContent = `${prefix}${t('dash_recent_media_image')}`;
              } else if (lastMsg.type === "AUDIO") {
                lastContent = `${prefix}${t('dash_recent_media_audio')}`;
              } else if (lastMsg.type === "FILE") {
                lastContent = `${prefix}${t('dash_recent_media_file')}`;
              } else if (lastMsg.type === "LETTER") {
                lastContent = `${prefix}${t('dash_recent_media_letter')}`;
              } else {
                let txt = lastMsg.content || "";
                // System message cleaning
                txt = txt
                  .replace(/VERIFIED PROFESSIONAL/gi, '')
                  .replace(/CERTIFIED PROFESSIONAL/gi, '')
                  .replace(/You are connected with a/gi, '')
                  .replace(/This space is monitored and safe/gi, '')
                  .replace(/You are not alone\./gi, '')
                  .trim();

                // 🧘 Soften the alarmist safety notice in preview
                if (txt.includes("SAFETY NOTICE")) {
                  txt = "Gentle support path • General focus only";
                }

                if (!txt) txt = t('dash_recent_msg_started');
                
                // Final fallback for raw URLs if type wasn't set correctly
                if (txt.startsWith('http') && txt.includes('supabase.co')) {
                   if (txt.match(/\.(jpg|jpeg|png|gif|webp)/i)) lastContent = `${prefix}${t('dash_recent_media_image')}`;
                   else if (txt.match(/\.(mp3|wav|ogg|m4a|webm)/i)) lastContent = `${prefix}${t('dash_recent_media_audio')}`;
                   else lastContent = `${prefix}${t('dash_recent_media_file')}`;
                } else {
                   lastContent = `${prefix}${txt}`;
                }
              }
            }

            return (
              <li key={chat.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all rounded-xl border-b border-gray-50 last:border-0 dark:border-gray-800">
                <Link to={`/chat?cid=${chat.id}`} className="flex items-start gap-3 p-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold shrink-0 border border-indigo-100/50 dark:border-indigo-800/30">
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-0.5">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <strong className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[120px] sm:max-w-none">{name}</strong>
                      {other?.role === "PROVIDER" && other?.provider?.isCertified === false && (
                        <div className="group relative">
                          <AlertCircle className="w-3.5 h-3.5 text-amber-500/70" />
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-[10px] text-white rounded-lg shadow-xl z-50 leading-relaxed">
                            This guide provides gentle, general support. Not certified for high-risk or crisis situations.
                          </div>
                        </div>
                      )}
                    </div>
                      <span className="text-[10px] text-gray-400 shrink-0">
                        {formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate line-clamp-1 leading-relaxed pr-2">
                      {lastContent}
                    </p>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}